import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from './ThemeProvider';
import { useCurrency } from './CurrencyProvider';
import { useLanguage } from '../i18n/LanguageContext';
import { Moon, Sun, ArrowUpRight, ChevronDown } from 'lucide-react';
import AnchorLink, { useActiveSection, TEAM_URL } from './AnchorLink';

const LinkedInIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

/** EN / JP segmented toggle. Switches the whole site's language. */
function LangToggle({ lang, setLang, className = '' }) {
  return (
    <div className={`of-lang-toggle ${className}`} role="group" aria-label="Language" data-no-translate>
      {[['en', 'EN'], ['ja', 'JP']].map(([code, label]) => (
        <button
          key={code}
          type="button"
          onClick={() => setLang(code)}
          aria-pressed={lang === code}
          className={lang === code ? 'is-active' : ''}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const NAV = [
  {
    label: 'Dashboard',
    to: '/dashboard',
    match: ['/dashboard'],
    menuTitle: 'Transparency Dashboard',
    children: [
      { label: 'Fund Metrics', desc: 'Deal flow, acceleration and investment', to: '/dashboard#fund-metrics' },
      { label: 'Ecosystem Metrics', desc: 'Live Cardano on-chain data', to: '/dashboard#ecosystem-metrics' },
      { label: 'Portfolio', desc: 'Companies backed by the fund', to: '/dashboard#portfolio' },
    ],
  },
  // Sections of the one-page landing
  { label: 'Growth', anchor: 'growth' },
  { label: 'Acceleration', anchor: 'acceleration' },
  { label: 'Team', href: TEAM_URL },
];

/** Desktop nav item with a Forward-style dropdown panel. Opens on hover or click. */
function NavDropdown({ item, active }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname, location.hash]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const show = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };
  const hide = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 140);
  };

  return (
    <div ref={ref} className="relative" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        className={`orion-nav-link inline-flex items-center gap-1 ${active ? 'is-active' : ''} ${open ? 'is-open' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {item.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`of-nav-menu ${open ? 'is-open' : ''}`} role="menu">
        <Link to={item.to} className="of-nav-menu-title" role="menuitem">
          {item.menuTitle || item.label} <ArrowUpRight className="w-3.5 h-3.5 opacity-60" />
        </Link>
        <div className="of-nav-menu-rule" />
        <div className="of-nav-menu-list">
          {item.children.map((c) => (
            <Link key={c.label} to={c.to} className="of-nav-menu-item" role="menuitem">
              <span className="of-nav-menu-label">{c.label}</span>
              <span className="of-nav-menu-desc">{c.desc}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function OrionHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/company');

  const { theme, toggleTheme } = useTheme();
  const { currency, toggleCurrency } = useCurrency();
  const { lang, setLang } = useLanguage();

  const activeSection = useActiveSection(location.pathname === '/');
  const isActive = (item) =>
    item.anchor ? activeSection === item.anchor : item.match?.some((m) => location.pathname.startsWith(m));

  // On the landing page the logo scrolls back to the top.
  const onLogoClick = (e) => {
    if (location.pathname !== '/') return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.history.replaceState(window.history.state, '', '/');
  };

  const renderLink = (item, className, onClick) =>
    item.anchor ? (
      <AnchorLink key={item.label} id={item.anchor} className={`${className} ${isActive(item) ? 'is-active' : ''}`} onClick={onClick}>
        {item.label}
      </AnchorLink>
    ) : item.href ? (
      <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {item.label}
      </a>
    ) : (
      <Link key={item.label} to={item.to} className={`${className} ${isActive(item) ? 'is-active' : ''}`} onClick={onClick}>
        {item.label}
      </Link>
    );

  return (
    <>
      {isMenuOpen && (
        <div className="fixed inset-0 bg-black/70 z-40 md:hidden" onClick={() => setIsMenuOpen(false)} />
      )}

      <header className="orion-header fixed top-0 left-0 right-0 z-50 border-b">
        <nav className="orion-header-nav mx-auto px-5 md:px-9">
          <div className="flex items-center justify-between h-16">
            {/* Logo + primary nav, left-aligned */}
            <div className="flex items-center gap-8 lg:gap-12">
              <Link to="/" onClick={onLogoClick} className="flex items-center flex-shrink-0" aria-label="Orion Fund home">
                <img src="/orion-logo.svg" alt="Orion Fund" width="165" height="36" fetchPriority="high" className="h-9 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6 lg:gap-8">
                {NAV.map((item) =>
                  item.children ? (
                    <NavDropdown key={item.label} item={item} active={isActive(item)} />
                  ) : (
                    renderLink(item, 'orion-nav-link')
                  )
                )}
              </div>
            </div>

            {/* Right side */}
            <div className="hidden md:flex items-center gap-3 flex-shrink-0">
              {isDashboard ? (
                <>
                  <button onClick={toggleCurrency} className="orion-control px-3 h-9 text-white">
                    {currency}
                  </button>
                  <LangToggle lang={lang} setLang={setLang} />
                  <button onClick={toggleTheme} className="orion-control w-9 h-9 inline-flex items-center justify-center text-white" aria-label="Toggle theme">
                    {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  </button>
                </>
              ) : (
                <>
                  <LangToggle lang={lang} setLang={setLang} className="mr-1" />
                  <a href="https://www.linkedin.com/company/draper-dragon/" target="_blank" rel="noopener noreferrer" className="p-2 text-[color:var(--of-muted)] hover:text-white transition-colors" aria-label="LinkedIn">
                    <LinkedInIcon className="w-4 h-4" />
                  </a>
                  <a href="https://x.com/OrionFund" target="_blank" rel="noopener noreferrer" className="p-2 text-[color:var(--of-muted)] hover:text-white transition-colors" aria-label="X (Twitter)">
                    <XIcon className="w-4 h-4" />
                  </a>
                  <Link to="/contact" className="of-btn ml-2 px-4 !min-h-[34px] text-[13px]">
                    Contact
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 -mr-2 text-white"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 8h16M4 16h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="md:hidden pb-6 border-t border-[color:var(--of-line)]">
              <div className="flex flex-col">
                {NAV.map((item) => (
                  <React.Fragment key={item.label}>
                    {renderLink(
                      item,
                      'flex items-center justify-between py-4 border-b border-[color:var(--of-line)] text-[15px] text-white/80',
                      () => setIsMenuOpen(false)
                    )}
                    {item.children?.map((c) => (
                      <Link
                        key={c.label}
                        to={c.to}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-between py-3 pl-4 border-b border-[color:var(--of-line)] text-[14px] text-white/60"
                      >
                        {c.label}
                        <span className="of-mono text-[11px] text-[color:var(--of-muted)]">{c.desc}</span>
                      </Link>
                    ))}
                  </React.Fragment>
                ))}
              </div>

              {isDashboard ? (
                <div className="flex items-center gap-2 pt-5">
                  <button onClick={toggleCurrency} className="orion-control flex-1 h-10 text-white">{currency}</button>

                  <button onClick={toggleTheme} className="orion-control w-10 h-10 inline-flex items-center justify-center text-white" aria-label="Toggle theme">
                    {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  </button>
                </div>
              ) : (
                <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="of-btn w-full mt-5">
                  Contact <ArrowUpRight className="w-4 h-4" />
                </Link>
              )}

              <div className="flex items-center gap-4 pt-5 text-[color:var(--of-muted)]">
                <LangToggle lang={lang} setLang={setLang} className="mr-auto" />
                <a href="https://www.linkedin.com/company/draper-dragon/" target="_blank" rel="noopener noreferrer" className="hover:text-white" aria-label="LinkedIn">
                  <LinkedInIcon className="w-5 h-5" />
                </a>
                <a href="https://x.com/OrionFund" target="_blank" rel="noopener noreferrer" className="hover:text-white" aria-label="X (Twitter)">
                  <XIcon className="w-5 h-5" />
                </a>
              </div>
            </div>
          )}
        </nav>
      </header>
    </>
  );
}
